import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, Button, IconButton, CircularProgress, LinearProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import { Link } from 'react-router-dom';
import { getClientBasicDetails, type ClientBasicDetailsResponse } from '@/lib/api/clients';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import { formatDisplayLabel } from '@/utils/display-labels';
import { Tag, TagList, type TagVariant } from '@/components/ui/tag';

interface ClientSidePanelProps {
  clientId: string | null;
  onClose: () => void;
}

type AppointmentSession = NonNullable<ClientBasicDetailsResponse['appointments']['lastSession']>;

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatTime = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const formatSessionWindow = (session?: AppointmentSession | null): string => {
  if (!session?.startTime) return '-';
  const date = formatDate(session.startTime);
  const start = formatTime(session.startTime);
  const end = formatTime(session.endTime);
  return [date, start && end ? `${start} - ${end}` : start].filter(Boolean).join(' · ');
};

const formatNumber = (value?: number | null, maximumFractionDigits = 1): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  return new Intl.NumberFormat(undefined, { maximumFractionDigits }).format(value);
};

const formatAge = (age?: number | null): string => {
  if (typeof age !== 'number' || !Number.isFinite(age)) return '-';
  return String(Math.floor(age));
};

const formatLabel = (value: string): string => formatDisplayLabel(value);

const formatList = (items?: unknown): string[] => {
  if (!items) return [];
  if (typeof items === 'string') {
    return items
      .split(',')
      .map((item) => formatLabel(item))
      .filter(Boolean);
  }
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      if (typeof item === 'string') return formatLabel(item);
      if (!item || typeof item !== 'object') return '';
      const record = item as Record<string, unknown>;
      return formatLabel(String(record.supplementName ?? record.conditionName ?? record.name ?? record.label ?? record.value ?? record.title ?? ''));
    })
    .filter(Boolean);
};

const firstText = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return formatLabel(value);
  }
  return '';
};

const InfoItem = ({ label, value, tone }: { label: string; value: React.ReactNode; tone?: string }) => (
  <Box>
    <Typography sx={{ fontSize: '0.875rem', color: '#64748b', mb: 0.5 }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: tone ?? '#1e293b', overflowWrap: 'anywhere' }}>
      {value}
    </Typography>
  </Box>
);

const ChipList = ({ values, tone = 'default' }: { values: string[]; tone?: 'default' | 'warning' | 'danger' }) => {
  const variantByTone: Record<'default' | 'warning' | 'danger', TagVariant> = {
    default: 'info',
    warning: 'warning',
    danger: 'danger',
  };

  return (
    <TagList gap="sm">
      {values.map((value) => (
        <Tag
          key={value}
          variant={variantByTone[tone]}
          size="sm"
          maxWidth="100%"
          className="rounded-md font-medium"
          truncate={false}
        >
          {value}
        </Tag>
      ))}
    </TagList>
  );
};

export const ClientSidePanel = ({ clientId, onClose }: ClientSidePanelProps) => {
  const [clientData, setClientData] = useState<ClientBasicDetailsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (clientId) {
      setLoading(true);
      getClientBasicDetails(clientId)
        .then((res) => {
          setClientData(res);
        })
        .catch((err) => {
          console.error('Failed to fetch client details:', err);
          setClientData(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setClientData(null);
    }
  }, [clientId]);

  const rawClientData = clientData as (ClientBasicDetailsResponse & Record<string, unknown>) | null;
  const rawClient = clientData?.client as (ClientBasicDetailsResponse['client'] & Record<string, unknown>) | undefined;
  const rawDietary = clientData?.dietary as (ClientBasicDetailsResponse['dietary'] & Record<string, unknown>) | undefined;
  const supplementNames = formatList(rawClientData?.supplements);
  const notes = clientData?.clientNotes?.filter((note) => note.content?.trim()) ?? [];

  const dietaryType = firstText(
    rawDietary?.dietaryType,
    rawDietary?.dietary_type,
    rawDietary?.dietaryName,
    rawDietary?.dietType,
  );
  const dietaryTags = formatList(clientData?.dietary?.dietaryTags);
  const allergies = formatList(clientData?.dietary?.allergies);
  const medicalConditions = formatList(
    rawDietary?.medicalConditions ?? rawClient?.medicalConditions ?? rawClientData?.medicalConditions,
  );
  const foodRestrictions = formatList(
    rawDietary?.foodRestrictions ??
      rawDietary?.food_restrictions ??
      rawDietary?.restrictions ??
      rawDietary?.foodPreferences ??
      rawClientData?.foodRestrictions,
  );
  const hasDietaryData =
    Boolean(dietaryType) ||
    dietaryTags.length > 0 ||
    allergies.length > 0 ||
    medicalConditions.length > 0 ||
    foodRestrictions.length > 0 ||
    supplementNames.length > 0;
  const hasAppointments = Boolean(clientData?.appointments?.lastSession || clientData?.appointments?.nextSession);
  const questionnaireProgress = Math.max(0, Math.min(100, clientData?.questionnaires?.completedPercentage ?? 0));

  return (
    <Drawer
      anchor="right"
      open={Boolean(clientId)}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 500, md: 594 }, bgcolor: '#f8fafc' },
      }}
    >
      <Box sx={{ p: 3, pt: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: '#1e293b' }}>
            Client Information
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
            <CircularProgress />
          </Box>
        ) : clientData ? (
          <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 4 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#1e293b', lineHeight: 1.2, overflowWrap: 'anywhere' }}>
                  <ClientNameLink clientId={clientId} className="text-[#1e293b] hover:text-[#1e293b]">
                    {clientData.client.fullName || 'Unnamed client'}
                  </ClientNameLink>
                </Typography>
                {clientData.client.email ? (
                  <Typography sx={{ mt: 0.75, fontSize: '0.875rem', color: '#64748b', overflowWrap: 'anywhere' }}>
                    {clientData.client.email}
                  </Typography>
                ) : null}
                {clientData.client.location ? (
                  <Typography sx={{ mt: 0.25, fontSize: '0.875rem', color: '#64748b', overflowWrap: 'anywhere' }}>
                    {clientData.client.location}
                  </Typography>
                ) : null}
              </Box>
              <Button
                component={Link}
                to={`/clients/${clientData.client.id}/details-v2/overview`}
                onClick={onClose}
                sx={{
                  bgcolor: '#25a8da',
                  color: '#fff',
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: 2,
                  py: 1,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: '#1498ca' },
                }}
              >
                View Full Profile
              </Button>
            </Box>

            <Box sx={{ mb: 4, bgcolor: '#fff', p: 3, borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b', mb: 2 }}>
                Anthropometric Data
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                <InfoItem label="Age" value={formatAge(clientData.client.age)} />
                <InfoItem label="Gender" value={clientData.client.gender || '-'} />
                <InfoItem label="Height" value={clientData.client.heightCm ? `${formatNumber(clientData.client.heightCm, 0)} cm` : '-'} />
                <InfoItem label="Weight" value={clientData.client.weightKg ? `${formatNumber(clientData.client.weightKg, 1)} kg` : '-'} />
                <InfoItem label="BMI" value={formatNumber(clientData.client.bmi, 1)} />
                <InfoItem label="BMR" value={clientData.client.bmr ? `${formatNumber(Math.round(clientData.client.bmr), 0)} kcal` : '-'} />
                <InfoItem label="Body Fat" value={clientData.client.bodyFatPercentage ? `${formatNumber(clientData.client.bodyFatPercentage, 1)}%` : '-'} />
              </Box>
            </Box>

            {clientData.mealPlan ? (
              <Box sx={{ mb: 4, bgcolor: '#fff', p: 3, borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 20 }} />
                  <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
                    Active Plan
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3 }}>
                  <InfoItem label="Meal Plan Name" value={clientData.mealPlan.name || '-'} />
                  <InfoItem label="Status" value={clientData.mealPlan.status || '-'} />
                  <InfoItem label="Start Date" value={formatDate(clientData.mealPlan.startDate)} />
                  <InfoItem label="End Date" value={formatDate(clientData.mealPlan.endDate)} />
                </Box>
                <Button
                  component={Link}
                  to={`/clients/${clientData.client.id}/details-v2/meal-plan`}
                  onClick={onClose}
                  fullWidth
                  sx={{
                    bgcolor: '#25a8da',
                    color: '#fff',
                    textTransform: 'none',
                    borderRadius: '8px',
                    '&:hover': { bgcolor: '#1498ca' },
                  }}
                >
                  View Meal Plan
                </Button>
              </Box>
            ) : null}

            {hasAppointments ? (
              <Box sx={{ mb: 4, bgcolor: '#fff', p: 3, borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <CalendarMonthOutlinedIcon sx={{ color: '#25a8da', fontSize: 20 }} />
                  <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
                    Appointments
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3 }}>
                  <InfoItem label="Last Session" value={formatSessionWindow(clientData.appointments.lastSession)} />
                  <InfoItem label="Next Session" value={formatSessionWindow(clientData.appointments.nextSession)} />
                </Box>
                {(clientData.appointments.nextSession?.notes || clientData.appointments.lastSession?.notes) ? (
                  <Typography sx={{ fontSize: '0.875rem', color: '#475569', mb: 3, overflowWrap: 'anywhere' }}>
                    {clientData.appointments.nextSession?.notes || clientData.appointments.lastSession?.notes}
                  </Typography>
                ) : null}
                <Button
                  component={Link}
                  to={`/clients/${clientData.client.id}/details-v2/appointments`}
                  onClick={onClose}
                  variant="outlined"
                  fullWidth
                  sx={{ borderColor: '#e2e8f0', color: '#475569', textTransform: 'none', borderRadius: '8px' }}
                >
                  View Appointments
                </Button>
              </Box>
            ) : null}

            {clientData.questionnaires ? (
              <Box sx={{ mb: 4, bgcolor: '#fff', p: 3, borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <AssignmentOutlinedIcon sx={{ color: '#22c55e', fontSize: 20 }} />
                  <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
                    Questionnaires
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3 }}>
                  <InfoItem label="Shared" value={clientData.questionnaires.totalShared} />
                  <InfoItem label="Completed" value={clientData.questionnaires.completed} />
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.875rem', color: '#64748b' }}>Completion</Typography>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#1e293b' }}>
                      {formatNumber(questionnaireProgress, 0)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={questionnaireProgress}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: '#e2e8f0',
                      '& .MuiLinearProgress-bar': { bgcolor: '#22c55e', borderRadius: 3 },
                    }}
                  />
                </Box>
                <Button
                  component={Link}
                  to={`/clients/${clientData.client.id}/details-v2/questionnaires`}
                  onClick={onClose}
                  variant="outlined"
                  fullWidth
                  sx={{ borderColor: '#e2e8f0', color: '#475569', textTransform: 'none', borderRadius: '8px' }}
                >
                  View Questionnaires
                </Button>
              </Box>
            ) : null}

            {hasDietaryData ? (
              <Box sx={{ mb: 4, bgcolor: '#fff', p: 3, borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <FavoriteBorderOutlinedIcon sx={{ color: '#ec4899', fontSize: 20 }} />
                  <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
                    Health and Preferences
                  </Typography>
                </Box>
                {dietaryType ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>Dietary Type</Typography>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#1e293b', overflowWrap: 'anywhere' }}>
                      {dietaryType}
                    </Typography>
                  </Box>
                ) : null}
                {dietaryTags.length > 0 ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 1 }}>Dietary Tags</Typography>
                    <ChipList values={dietaryTags} />
                  </Box>
                ) : null}
                {allergies.length > 0 ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 1 }}>Allergies</Typography>
                    <ChipList values={allergies} tone="danger" />
                  </Box>
                ) : null}
                {medicalConditions.length > 0 ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 1 }}>Medical Conditions</Typography>
                    <ChipList values={medicalConditions} tone="warning" />
                  </Box>
                ) : null}
                {foodRestrictions.length > 0 ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 1 }}>Food Restrictions</Typography>
                    <ChipList values={foodRestrictions} tone="warning" />
                  </Box>
                ) : null}
                {supplementNames.length > 0 ? (
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 1 }}>Supplements</Typography>
                    <ChipList values={supplementNames} />
                  </Box>
                ) : null}
              </Box>
            ) : null}

            {notes.length > 0 ? (
              <Box sx={{ mb: 4, bgcolor: '#fff', p: 3, borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <NoteAltOutlinedIcon sx={{ color: '#22c55e', fontSize: 20 }} />
                  <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
                    Client Notes
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  {notes.slice(0, 3).map((note, index) => (
                    <Box key={note.id ?? `${note.createdAt}-${index}`}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>
                        {[formatDate(note.createdAt), note.authorName || note.createdBy].filter(Boolean).join(' - ')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.875rem', color: '#334155', overflowWrap: 'anywhere' }}>
                        {note.content}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Button
                  component={Link}
                  to={`/clients/${clientData.client.id}/details-v2/flags-and-notes`}
                  onClick={onClose}
                  variant="outlined"
                  fullWidth
                  sx={{ borderColor: '#e2e8f0', color: '#475569', textTransform: 'none', borderRadius: '8px' }}
                >
                  View Notes
                </Button>
              </Box>
            ) : null}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 5, color: '#64748b' }}>
            Client not found
          </Box>
        )}
      </Box>
    </Drawer>
  );
};
